import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, ShieldCheck, MessageCircleQuestion } from "lucide-react";
import { Button } from "@/components/ui/button";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";

const PLANS = {
  individuals: [
    {
      name: "Solo",
      price: 29,
      annualPrice: 25,
      annualTotal: 299,
      tagline: "For freelancers and solo founders",
      perks: ["250 prospects / month", "ICP scoring & filters", "10 outreach drafts / day", "CSV export", "Native send via Resend", "Email support"],
      testid: "plan-solo",
    },
    {
      name: "Pro",
      price: 79,
      annualPrice: 66,
      annualTotal: 799,
      highlight: true,
      tagline: "For full-time sellers and consultants",
      perks: ["1,000 prospects / month", "Unlimited outreach drafting", "CSV upload + bulk personalise", "Bulk Send All per campaign", "Multi-campaign workspace", "Priority support"],
      testid: "plan-pro",
    },
  ],
  business: [
    {
      name: "Team",
      price: 199,
      annualPrice: 166,
      annualTotal: 1999,
      tagline: "For startups and small sales teams",
      perks: ["3,000 prospects / month", "3 team seats", "Shared campaigns & lead pools", "Bulk drafting & sending", "Custom From identities", "Onboarding session", "Priority support"],
      testid: "plan-team",
    },
    {
      name: "Scale",
      price: 499,
      annualPrice: 416,
      annualTotal: 4999,
      highlight: true,
      tagline: "For agencies and revenue orgs",
      perks: ["10,000 prospects / month", "10 team seats", "Workspace roles & permissions", "Dedicated success manager", "Custom integrations & webhooks", "SLA + Slack channel"],
      testid: "plan-scale",
    },
  ],
};

const FAQ = [
  { q: "How are 'prospects' counted?", a: "Each lead surfaced in a campaign or imported via CSV counts as one prospect. Unused prospects don't roll over month-to-month." },
  { q: "Can I bring my own domain for sending?", a: "Yes. Connect your Resend account, verify your domain, set a From address in Settings — every email ships from your verified identity." },
  { q: "Do you offer annual discounts?", a: "Yes — switching to annual billing knocks ~20% off any plan. Toggle annual at checkout once Stripe is live, or contact us in the meantime." },
  { q: "Is my data private?", a: "Your campaigns, leads and outreach are scoped to your account. We never share or sell your prospect lists, and you can export or delete data at any time." },
  { q: "Can I switch tiers later?", a: "Anytime. Upgrades take effect immediately. Downgrades take effect at the next billing cycle." },
];

export default function Pricing() {
  const [tab, setTab] = useState("individuals");
  const [interval, setInterval] = useState("month"); // 'individuals' | 'business'

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-[#0A0A0F]">
      <MarketingHeader />

      <section className="relative overflow-hidden py-20 md:py-24">
        <div className="absolute inset-0 dot-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center fade-in-up">
          <div className="label-mono mb-4">— Pricing</div>
          <h1 className="font-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.98]">
            One workspace.<br />
            <span className="text-[#5C5C66]">Every team size.</span>
          </h1>
          <p className="mt-6 text-[#5C5C66] text-lg max-w-2xl mx-auto">
            Start free. Upgrade when you're ready. Cancel anytime — no contracts, no surprises.
          </p>

          {/* Tabs and Toggle */}
          <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="inline-flex items-center bg-white border border-[#EAEAE3] rounded-full p-1 shadow-soft" data-testid="pricing-tabs">
              <button
                onClick={() => setTab("individuals")}
                data-testid="tab-individuals"
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${tab === "individuals" ? "bg-[#0A0A0F] text-white shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F]"}`}
              >
                For individuals
              </button>
              <button
                onClick={() => setTab("business")}
                data-testid="tab-business"
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${tab === "business" ? "bg-[#0A0A0F] text-white shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F]"}`}
              >
                For business
              </button>
            </div>
            
            <div className="hidden md:block w-px h-8 bg-[#EAEAE3]"></div>

            <div className="relative inline-flex items-center bg-white border border-[#EAEAE3] rounded-full p-1 shadow-soft">
              {interval === "year" && (
                <div className="absolute -top-5 right-2 text-[10px] font-mono font-bold text-green-600 uppercase tracking-widest whitespace-nowrap">
                  ✨ 2 Months Free
                </div>
              )}
              <button
                onClick={() => setInterval("month")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${interval === "month" ? "bg-[#0A0A0F] text-white shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F]"}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setInterval("year")}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${interval === "year" ? "bg-[#0A0A0F] text-white shadow-soft" : "text-[#5C5C66] hover:text-[#0A0A0F]"}`}
              >
                Annual
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {PLANS[tab].map((p) => <PlanCard key={p.name} {...p} interval={interval} />)}
          </div>

          <div className="mt-10 text-center">
            <p className="text-sm text-[#5C5C66]">Need something custom? <a href="mailto:support@leadsb2b.app" className="text-[#0A0A0F] font-medium underline underline-offset-4 hover:text-[#002FA7]" data-testid="contact-sales">Talk to us</a>.</p>
          </div>
        </div>
      </section>

      {/* Compare features */}
      <section className="py-20 border-t border-[#EAEAE3] bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="label-mono mb-3">— What's included</div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">In every plan</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { t: "Lead scoring", b: "1–100 fit score against your stated ICP, refreshed on every campaign." },
              { t: "CSV in / out", b: "Bring your existing lists, export anytime — never locked in." },
              { t: "Personalised drafting", b: "Tone-tuned cold email drafts referencing each prospect's role, company and pain points." },
              { t: "Native sending", b: "Connect Resend once, send through your verified domain. Replies hit your inbox." },
              { t: "Pipeline status", b: "Track new → contacted → replied → qualified across every campaign." },
              { t: "Workspace history", b: "Every prospect, draft and send preserved. Audit, re-export, repeat." },
            ].map((f) => (
              <div key={f.t} className="rounded-xl border border-[#EAEAE3] p-6 hover:border-[#0A0A0F]/20 transition-colors">
                <ShieldCheck className="h-5 w-5 text-[#002FA7] mb-3" />
                <div className="font-display text-lg font-bold mb-1.5">{f.t}</div>
                <div className="text-sm text-[#5C5C66] leading-relaxed">{f.b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <MessageCircleQuestion className="h-6 w-6 text-[#002FA7] mx-auto mb-3" />
            <div className="label-mono mb-3">— FAQ</div>
            <h2 className="font-display text-3xl md:text-4xl font-black tracking-tight">Common questions</h2>
          </div>
          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-xl border border-[#EAEAE3] bg-white p-5 transition-colors open:bg-white open:border-[#0A0A0F]/30" data-testid={`faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none font-display font-semibold text-[#0A0A0F]">
                  {f.q}
                  <span className="text-[#9A9AA3] group-open:rotate-45 transition-transform text-2xl leading-none">+</span>
                </summary>
                <p className="mt-3 text-sm text-[#5C5C66] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 dark-section relative overflow-hidden">
        <div className="absolute inset-0 dot-bg-dark opacity-50" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center text-white">
          <h2 className="font-display text-4xl md:text-5xl font-black tracking-tighter leading-tight">
            Try it free for 7 days.
          </h2>
          <p className="mt-4 text-white/70 text-lg max-w-xl mx-auto">No credit card. Full access. We won't bill you anything until you're convinced.</p>
          <div className="mt-8">
            <Link to="/signup" data-testid="pricing-final-cta">
              <Button className="rounded-lg h-12 px-8 bg-white text-[#0A0A0F] hover:bg-white/90 text-base font-medium">
                Start free trial <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}

function PlanCard({ name, price, annualPrice, annualTotal, tagline, perks, highlight, testid, interval }) {
  const displayPrice = interval === "year" ? annualPrice : price;
  return (
    <div data-testid={testid} className={`relative flex flex-col rounded-2xl p-8 transition-all duration-300 hover:-translate-y-0.5 ${highlight ? "bg-[#0A0A0F] text-white border border-[#0A0A0F] shadow-soft-lg" : "bg-white border border-[#EAEAE3] shadow-soft"}`}>
      {highlight && (
        <span className="absolute top-0 right-7 -translate-y-1/2 bg-[#FF5722] text-white text-[10px] font-mono uppercase tracking-widest px-3 py-1 rounded-md">Most popular</span>
      )}
      <div className={`label-mono mb-2 ${highlight ? "!text-white/60" : ""}`}>{name}</div>
      <div className="flex items-baseline gap-1.5 mb-2">
        <span className="font-display text-5xl font-black tracking-tighter">€{displayPrice}</span>
        <span className={`${highlight ? "text-white/50" : "text-[#9A9AA3]"} text-sm`}>/ mo</span>
      </div>
      {interval === "year" && (
        <div className={`text-xs mb-6 ${highlight ? "text-white/60" : "text-[#5C5C66]"}`}>
          Billed €{annualTotal} annually
        </div>
      )}
      {interval === "month" && (
        <div className={`text-xs mb-6 opacity-0`}>Spacer</div>
      )}
      <p className={`text-sm mb-7 ${highlight ? "text-white/70" : "text-[#5C5C66]"}`}>{tagline}</p>
      <Link to="/signup">
        <Button className={`w-full rounded-lg mb-7 ${highlight ? "bg-white text-[#0A0A0F] hover:bg-white/90" : "bg-[#0A0A0F] text-white hover:bg-[#14141B]"}`}>
          Start free trial
        </Button>
      </Link>
      <ul className="space-y-3">
        {perks.map((p, i) => (
          <li key={i} className={`text-sm flex items-start gap-2.5 ${highlight ? "text-white/85" : "text-[#0A0A0F]"}`}>
            <Check className={`h-4 w-4 mt-0.5 shrink-0 ${highlight ? "text-[#FF5722]" : "text-[#002FA7]"}`} />
            {p}
          </li>
        ))}
      </ul>
    </div>
  );
}
